import React, { useEffect, useState } from "react";
import axios from "axios";
import Shell from "@/components/Shell";
import { API, formatErr, useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UsersThree, Shield, EnvelopeSimple, Plus, TrashSimple, Wrench,
} from "@phosphor-icons/react";

function WhitelistDialog({ open, setOpen, rows, rootEmail, onChanged }) {
  const [email, setEmail] = useState("");
  const confirm = useConfirm();

  const add = async () => {
    const em = email.trim().toLowerCase();
    if (!em) return;
    try {
      await axios.post(`${API}/ortaklar-carisi/whitelist`, { email: em });
      toast.success("Yetki verildi");
      setEmail("");
      onChanged();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (w) => {
    const ok = await confirm({ title: "Yetkiyi kaldır", description: `${w.email} yetkisi kaldırılsın mı?`,
                               confirmText: "Kaldır", destructive: true });
    if (!ok) return;
    try {
      await axios.delete(`${API}/ortaklar-carisi/whitelist/${w.id}`);
      toast.success("Yetki kaldırıldı");
      onChanged();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg rounded-none sm:rounded-sm" data-testid="ortak-whitelist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} weight="duotone" className="text-sky-600" />
            Ortaklar Carisi — Yetkililer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-2.5 bg-slate-50 border border-slate-200 text-xs flex items-center gap-2">
            <EnvelopeSimple size={14} weight="bold" className="text-sky-700" />
            <span className="font-bold text-[#1B4776]">Root:</span>
            <span className="font-mono" data-testid="ortak-root-email">{rootEmail}</span>
            <span className="ml-auto text-[9px] uppercase tracking-widest font-bold bg-sky-100 text-sky-800 px-1.5 py-0.5 border border-sky-300">
              Kalıcı
            </span>
          </div>
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && add()}
                   placeholder="E-posta ile yetki ver" className="rounded-none sm:rounded-sm"
                   data-testid="ortak-wl-email-input" />
            <Button onClick={add} className="bg-sky-700 hover:bg-sky-800 text-white rounded-none sm:rounded-sm"
                    data-testid="ortak-wl-add-btn">
              <Plus size={14} weight="bold" className="mr-1" /> Ekle
            </Button>
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">Henüz başka yetkili yok.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-auto">
              {rows.map((w) => (
                <div key={w.id} className="flex items-center justify-between gap-2 p-2 border border-slate-200 bg-white"
                     data-testid={`ortak-wl-row-${w.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-mono truncate">{w.email}</div>
                    <div className="text-[10px] text-slate-400">{w.added_by} · {(w.added_at || "").slice(0, 10)}</div>
                  </div>
                  <button onClick={() => remove(w)} className="p-1.5 text-slate-400 hover:text-rose-700"
                          data-testid={`ortak-wl-delete-${w.id}`}>
                    <TrashSimple size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrtaklarCarisi() {
  const { ortakAccess } = useAuth();
  const isRoot = !!ortakAccess?.is_root;
  const [wlOpen, setWlOpen] = useState(false);
  const [wl, setWl] = useState({ entries: [], root_email: "" });

  const loadWhitelist = async () => {
    try {
      const { data } = await axios.get(`${API}/ortaklar-carisi/whitelist`);
      setWl(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { if (wlOpen) loadWhitelist(); }, [wlOpen]);

  return (
    <Shell hideSiteSelector>
      <div className="space-y-6" data-testid="ortak-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-sky-500/15 flex items-center justify-center">
              <UsersThree size={22} weight="duotone" className="text-sky-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1B4776]">Ortaklar Carisi</h1>
              <p className="text-sm text-slate-500">
                Gizli modül · {isRoot ? "Root yetkisi" : "Yetkili"}
              </p>
            </div>
          </div>
          {isRoot && (
            <Button variant="outline" onClick={() => setWlOpen(true)} className="rounded-none sm:rounded-sm"
                    data-testid="ortak-whitelist-btn">
              <Shield size={14} weight="bold" className="mr-1.5" /> Yetkililer
            </Button>
          )}
        </div>

        <Card className="p-12 flex flex-col items-center text-center gap-4 border-dashed border-2 border-sky-200 bg-sky-50/40 rounded-none sm:rounded-sm"
              data-testid="ortak-under-development">
          <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center">
            <Wrench size={28} weight="duotone" className="text-sky-700" />
          </div>
          <div>
            <div className="text-lg font-bold text-[#1B4776]">Bu modül şimdilik geliştirme aşamasındadır.</div>
            <p className="text-sm text-slate-500 mt-1">Ortaklar Carisi özellikleri yakında burada olacak.</p>
          </div>
        </Card>
      </div>

      {isRoot && (
        <WhitelistDialog open={wlOpen} setOpen={setWlOpen} rows={wl.entries || []}
                         rootEmail={wl.root_email} onChanged={loadWhitelist} />
      )}
    </Shell>
  );
}
